<script setup lang="ts">
import { computed } from 'vue';
import Checkbox from 'primevue/checkbox';
import Message from 'primevue/message';
import RadioButton from 'primevue/radiobutton';
import Tag from 'primevue/tag';
import { Column, Columns, Stack, Typography } from '@apparatix/library/core';
import type { MergeChoice, MergeFieldKey, MergeNoteChoice, MergePreview } from '@apparatix/types/Contacts';
import { mergeFieldDefinitions, mergeLockedFieldKeys, type MergeValueOption } from './mergeWizardHelpers';

type Props = {
  preview: MergePreview;
  emailOptions: MergeValueOption[];
  phoneNumberOptions: MergeValueOption[];
  webPageOptions: MergeValueOption[];
}

const props = defineProps<Props>();

const fieldChoices = defineModel<Record<MergeFieldKey, MergeChoice>>('fieldChoices', { required: true });
const addressChoice = defineModel<MergeChoice>('addressChoice', { required: true });
const keptEmailIds = defineModel<number[]>('keptEmailIds', { required: true });
const keptPhoneNumberIds = defineModel<number[]>('keptPhoneNumberIds', { required: true });
const keptWebPageIds = defineModel<number[]>('keptWebPageIds', { required: true });
const noteChoice = defineModel<MergeNoteChoice>('noteChoice', { required: true });

const normalizeChoiceValue = (value?: string | null): string => String(value ?? '').trim();
const hasValue = (value?: string | null): boolean => normalizeChoiceValue(value) !== '';
const valuesAreSame = (sourceValue?: string | null, selectedValue?: string | null): boolean => hasValue(sourceValue)
  && hasValue(selectedValue)
  && normalizeChoiceValue(sourceValue) === normalizeChoiceValue(selectedValue);

const sourceName = computed(() => props.preview.sourceContact.fullName);

// Shown to write-access users who aren't blocked from editing these fields, but should still
// know the source contact is the billing contact on an account with active contracts.
const sourceBillingAccountNames = computed(() => props.preview.sourceBillingAccounts.map(account => account.c2Name).join(', '));

const fieldRows = computed(() => mergeFieldDefinitions
  .map(definition => {
    const sourceValue = definition.getValue(props.preview.sourceContact);
    const selectedValue = definition.getValue(props.preview.selectedContact);

    return {
      key: definition.key,
      label: definition.label,
      sourceValue,
      selectedValue,
      valuesMatch: definition.key !== 'owner' && valuesAreSame(sourceValue, selectedValue),
      locked: props.preview.sourceInvoiceLocked && mergeLockedFieldKeys.includes(definition.key),
    };
  })
  .filter(row => hasValue(row.sourceValue) || hasValue(row.selectedValue))
  .filter(row => row.key !== 'owner' || props.preview.sourceContact.userId !== props.preview.selectedContact.userId));

const addressRow = computed(() => {
  const sourceValue = props.preview.sourceContact.addressFormatted;
  const selectedValue = props.preview.selectedContact.addressFormatted;

  return {
    sourceValue,
    selectedValue,
    valuesMatch: valuesAreSame(sourceValue, selectedValue),
    locked: props.preview.sourceInvoiceLocked,
  };
});

const showAddressRow = computed(() => hasValue(addressRow.value.sourceValue) || hasValue(addressRow.value.selectedValue));

const sourceNoteText = computed(() => props.preview.sourceContact.notes?.[0]?.note ?? '');
const selectedNoteText = computed(() => props.preview.selectedContact.notes?.[0]?.note ?? '');
const showNotes = computed(() => hasValue(sourceNoteText.value) || hasValue(selectedNoteText.value));
const notesMatch = computed(() => valuesAreSame(sourceNoteText.value, selectedNoteText.value));

const originTagLabel = (option: MergeValueOption): string | null => {
  if (option.origin === 'selected') return 'From Selected Contact';
  if (option.origin === 'both') return 'On both';
  return null;
};
</script>
<template>
  <Stack :gap="5">
    <Message v-if="props.preview.sourceInvoiceLocked" severity="info">
      {{ sourceName }} has ongoing invoices, so its name, address, emails, and phone numbers are locked and will be kept exactly as they are.
    </Message>
    <Message v-else-if="props.preview.sourceBillingAccounts.length" severity="warn">
      {{ sourceName }} is the billing contact for active contracts on: {{ sourceBillingAccountNames }}. Changing its name, address, emails, or phone numbers here will change billing information for those accounts.
    </Message>
    <Columns class="lg:gap-x-8">
      <Column>
        <Stack :gap="4">
          <Typography variant="small-subtitle" muted>
            Pick which value to keep for each field. Values from {{ sourceName }} are kept by default.
          </Typography>
          <div
            v-for="row in fieldRows"
            :key="row.key"
            class="flex flex-col gap-2 pb-3 border-b border-[var(--p-content-border-color)]"
          >
            <Typography variant="form-label">
              {{ row.label }}
            </Typography>
            <div v-if="row.valuesMatch" class="flex items-center gap-2">
              <span>{{ row.sourceValue }}</span>
              <Tag value="On both" severity="info" />
            </div>
            <div v-else class="flex flex-col gap-2 lg:flex-row lg:gap-8">
              <label class="flex items-center gap-2" :class="{ 'opacity-60': row.locked }">
                <RadioButton
                  v-model="fieldChoices[row.key]"
                  :value="'source'"
                  :disabled="row.locked"
                  :input-id="`merge-field-${row.key}-source`"
                />
                <span>{{ hasValue(row.sourceValue) ? row.sourceValue : '(empty)' }}</span>
              </label>
              <label class="flex items-center gap-2" :class="{ 'opacity-60': row.locked }">
                <RadioButton
                  v-model="fieldChoices[row.key]"
                  :value="'selected'"
                  :disabled="row.locked"
                  :input-id="`merge-field-${row.key}-selected`"
                />
                <span>{{ hasValue(row.selectedValue) ? row.selectedValue : '(empty)' }}</span>
                <Tag value="From Selected Contact" severity="secondary" />
              </label>
            </div>
          </div>
          <div v-if="showAddressRow" class="flex flex-col gap-2 pb-3 border-b border-[var(--p-content-border-color)]">
            <Typography variant="form-label">
              Address
            </Typography>
            <div v-if="addressRow.valuesMatch" class="flex items-center gap-2">
              <span>{{ addressRow.sourceValue }}</span>
              <Tag value="On both" severity="info" />
            </div>
            <div v-else class="flex flex-col gap-2 lg:flex-row lg:gap-8">
              <label class="flex items-center gap-2" :class="{ 'opacity-60': addressRow.locked }">
                <RadioButton
                  v-model="addressChoice"
                  :value="'source'"
                  :disabled="addressRow.locked"
                  input-id="merge-address-source"
                />
                <span>{{ hasValue(addressRow.sourceValue) ? addressRow.sourceValue : '(empty)' }}</span>
              </label>
              <label class="flex items-center gap-2" :class="{ 'opacity-60': addressRow.locked }">
                <RadioButton
                  v-model="addressChoice"
                  :value="'selected'"
                  :disabled="addressRow.locked"
                  input-id="merge-address-selected"
                />
                <span>{{ hasValue(addressRow.selectedValue) ? addressRow.selectedValue : '(empty)' }}</span>
                <Tag value="From Selected Contact" severity="secondary" />
              </label>
            </div>
          </div>
        </Stack>
      </Column>
      <Column>
        <Stack :gap="5">
          <Stack :gap="2">
            <Typography variant="section-heading">
              Emails
            </Typography>
            <label
              v-for="option in props.emailOptions"
              :key="`email-${option.id}`"
              class="flex items-center gap-2"
              :class="{ 'opacity-60': props.preview.sourceInvoiceLocked }"
            >
              <Checkbox
                v-model="keptEmailIds"
                :value="option.id"
                :disabled="props.preview.sourceInvoiceLocked"
              />
              <span>{{ option.label }}</span>
              <Tag v-if="option.addressType" :value="option.addressType" severity="secondary" />
              <Tag v-if="originTagLabel(option)" :value="originTagLabel(option)" severity="info" />
            </label>
            <Typography v-if="!props.emailOptions.length" variant="small-subtitle" muted>
              Neither contact has an email.
            </Typography>
          </Stack>
          <Stack :gap="2">
            <Typography variant="section-heading">
              Phone Numbers
            </Typography>
            <label
              v-for="option in props.phoneNumberOptions"
              :key="`phone-${option.id}`"
              class="flex items-center gap-2"
              :class="{ 'opacity-60': props.preview.sourceInvoiceLocked }"
            >
              <Checkbox
                v-model="keptPhoneNumberIds"
                :value="option.id"
                :disabled="props.preview.sourceInvoiceLocked"
              />
              <span>{{ option.label }}</span>
              <Tag v-if="option.addressType" :value="option.addressType" severity="secondary" />
              <Tag v-if="originTagLabel(option)" :value="originTagLabel(option)" severity="info" />
            </label>
            <Typography v-if="!props.phoneNumberOptions.length" variant="small-subtitle" muted>
              Neither contact has a phone number.
            </Typography>
          </Stack>
          <Stack :gap="2">
            <Typography variant="section-heading">
              Websites
            </Typography>
            <label
              v-for="option in props.webPageOptions"
              :key="`webPage-${option.id}`"
              class="flex items-center gap-2"
            >
              <Checkbox v-model="keptWebPageIds" :value="option.id" />
              <span>{{ option.label }}</span>
              <Tag v-if="option.addressType" :value="option.addressType" severity="secondary" />
              <Tag v-if="originTagLabel(option)" :value="originTagLabel(option)" severity="info" />
            </label>
            <Typography v-if="!props.webPageOptions.length" variant="small-subtitle" muted>
              Neither contact has a website.
            </Typography>
          </Stack>
          <Stack v-if="showNotes" :gap="2">
            <Typography variant="section-heading">
              Note
            </Typography>
            <div v-if="notesMatch" class="flex items-start gap-2">
              <Typography variant="small-subtitle" muted>
                {{ sourceNoteText }}
              </Typography>
              <Tag value="On both" severity="info" />
            </div>
            <template v-else>
              <label class="flex items-start gap-2">
                <RadioButton v-model="noteChoice" :value="'source'" input-id="merge-note-source" />
                <span class="flex flex-col">
                  <span>Keep note from {{ sourceName }}</span>
                  <Typography v-if="hasValue(sourceNoteText)" variant="small-subtitle" muted>
                    {{ sourceNoteText }}
                  </Typography>
                </span>
              </label>
              <label class="flex items-start gap-2">
                <RadioButton v-model="noteChoice" :value="'selected'" input-id="merge-note-selected" />
                <span class="flex flex-col">
                  <span>Use note from the Selected Contact</span>
                  <Typography v-if="hasValue(selectedNoteText)" variant="small-subtitle" muted>
                    {{ selectedNoteText }}
                  </Typography>
                </span>
              </label>
              <label class="flex items-center gap-2">
                <RadioButton v-model="noteChoice" :value="'combine'" input-id="merge-note-combine" />
                <span>Combine both notes</span>
              </label>
            </template>
          </Stack>
        </Stack>
      </Column>
    </Columns>
  </Stack>
</template>
